import React, { useState, useRef, createElement } from 'react';
import { DownloadIcon } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import DashboardLayout from '../../components/layout/DashboardLayout';
// Helper function to calculate CRC16-CCITT
const calculateCRC = (data: string): string => {
  let crc = 0xffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        crc = crc << 1 ^ 0x1021;
      } else {
        crc = crc << 1;
      }
    }
  }
  return (crc & 0xffff).toString(16).toUpperCase().padStart(4, '0');
};
// Helper function to format EMV tag-length-value
const formatTLV = (tag: string, value: string): string => {
  const length = value.length.toString().padStart(2, '0');
  return `${tag}${length}${value}`;
};
// Generate EMV Co. compliant QR code string with amount
const generateEMVQRString = (merchantData: any, amount: string): string => {
  let qrString = '';
  // Tag 00: Payload Format Indicator
  qrString += formatTLV('00', '01');
  // Tag 01: Point of Initiation Method (12 = Static)
  qrString += formatTLV('01', '12');
  // Tag 26-51: Merchant Account Information
  const merchantAccountInfo = formatTLV('00', 'PK') + formatTLV('01', merchantData.mid) + formatTLV('02', merchantData.tid);
  qrString += formatTLV('26', merchantAccountInfo);
  // Tag 52: Merchant Category Code
  qrString += formatTLV('52', '5204');
  // Tag 53: Transaction Currency (586 = PKR)
  qrString += formatTLV('53', '586');
  // Tag 54: Transaction Amount
  if (amount) {
    qrString += formatTLV('54', amount);
  }
  // Tag 58: Country Code
  qrString += formatTLV('58', 'PK');
  // Tag 59: Merchant Name
  qrString += formatTLV('59', merchantData.name);
  // Tag 60: Merchant City
  qrString += formatTLV('60', 'Karachi');
  // Tag 63: CRC (placeholder)
  qrString += '6304';
  // Calculate and append CRC
  const crc = calculateCRC(qrString);
  qrString += crc;
  return qrString;
};
const StaticAmountQRCPage = () => {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [amount, setAmount] = useState('');
  const [showQR, setShowQR] = useState(false);
  const merchantData = {
    name: 'AQS Store',
    mid: '050102100023650',
    tid: '2365010001'
  };
  const handleGenerate = e => {
    e.preventDefault();
    if (amount) {
      setShowQR(true);
    }
  };
  const handleDownload = () => {
    if (qrCodeRef.current) {
      toPng(qrCodeRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ffffff'
      }).then(dataUrl => {
        const link = document.createElement('a');
        link.download = `QR-Static-Amount-${merchantData.mid}-${amount}.png`;
        link.href = dataUrl;
        link.click();
      }).catch(err => {
        console.error('Error downloading QR code:', err);
      });
    }
  };
  const qrCodeString = showQR ? generateEMVQRString(merchantData, amount) : '';
  return <DashboardLayout data-id="element-1664">
      <div className="mb-6" data-id="element-1665">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1666">
          Static QR Code with Amount
        </h1>
        <p className="text-gray-600" data-id="element-1667">
          Generate a static QR code with a specified amount
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1668">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-1669">
          <h2 className="text-lg font-semibold mb-4" data-id="element-1670">QR Code Details</h2>
          <form onSubmit={handleGenerate} data-id="element-1671">
            <div className="space-y-4" data-id="element-1672">
              <div data-id="element-1673">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1674">
                  Merchant Name
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1675">
                  {merchantData.name}
                </div>
              </div>
              <div data-id="element-1676">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1677">
                  Merchant ID
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1678">
                  {merchantData.mid}
                </div>
              </div>
              <div data-id="element-1679">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1680">
                  Till ID
                </label>
                <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-1681">
                  {merchantData.tid}
                </div>
              </div>
              <div data-id="element-1682">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1683">
                  Amount (PKR)
                </label>
                <input type="number" id="amount" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter amount" required data-id="element-1684" />
              </div>
              <div data-id="element-1685">
                <button type="submit" className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1686">
                  Generate QR Code
                </button>
              </div>
            </div>
          </form>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 flex flex-col items-center justify-center" data-id="element-1687">
          {showQR ? <>
              <div ref={qrCodeRef} className="bg-white p-6 flex flex-col items-center" data-id="element-1688">
                <div className="mb-4 p-4 bg-white border-2 border-gray-300 rounded-lg inline-block" data-id="element-1689">
                  <QRCodeSVG id="qr-code-canvas" value={qrCodeString} size={256} level="M" includeMargin={true} fgColor="#000000" bgColor="#FFFFFF" data-id="element-1690" />
                </div>
                <p className="text-center text-gray-700 mb-2" data-id="element-1691">
                  Scan this QR code to make a payment
                </p>
                <p className="text-center text-lg font-semibold text-blue-600 mb-6" data-id="element-1692">
                  ₨{amount}
                </p>
                <div className="flex justify-center items-center space-x-4" data-id="element-1693">
                  <img src="https://cdn.magicpatterns.com/uploads/uyg5nASZDGjrCe2rJaNmzm/Visa_Logo.png" alt="Visa" className="h-8 object-contain" data-id="element-1694" />
                  <img src="https://cdn.magicpatterns.com/uploads/wTaFUzAaMXmqM9o6YPo8t5/MasterCard_Logo.png" alt="MasterCard" className="h-8 object-contain" data-id="element-1695" />
                  <img src="https://cdn.magicpatterns.com/uploads/8qxbwmnN4pa5FLphCCtDLR/UnionPay_Logo.png" alt="UnionPay" className="h-8 object-contain" data-id="element-1696" />
                  <img src="https://cdn.magicpatterns.com/uploads/vGk29PVKWf7oAsHTkLwRwU/PayPak_Logo.png" alt="PayPak" className="h-8 object-contain" data-id="element-1697" />
                  <img src="https://cdn.magicpatterns.com/uploads/dW1dUF5BFKkWuGeGttfoz6/RAAST_Logo.png" alt="RAAST" className="h-8 object-contain" data-id="element-1698" />
                </div>
              </div>
              <div className="mt-6" data-id="element-1699">
                <button onClick={handleDownload} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1700">
                  <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1701" />
                  Download QR Code
                </button>
              </div>
            </> : <div className="text-center text-gray-500" data-id="element-1702">
              <div className="h-64 w-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4" data-id="element-1703">
                <p data-id="element-1704">QR Code will appear here</p>
              </div>
              <p data-id="element-1705">Enter an amount and generate a QR code</p>
            </div>}
        </div>
      </div>
    </DashboardLayout>;
};
export default StaticAmountQRCPage;